import React, { useState, useEffect } from "react";
import { Autocomplete } from "@react-google-maps/api";
import axios from "axios";
import "./AIVoiceAgent.css";
import { API_URL } from "../config";
import TRUCK_TYPES from "../assets/truck_types.js";
import STATE_NAME_TO_CODE from "../assets/state_name_to_code.js";

const AIVoiceAgent = () => {
  const [preferences, setPreferences] = useState({
    is_active: false,
    phone_number: "",
    truck_type: "V",
    origin_city: "",
    origin_state: "",
    max_weight: 45000,
    min_rate_per_mile: 0,
    max_deadhead: 0,
    user_id: JSON.parse(localStorage.getItem("user"))?.email || "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [originAutocomplete, setOriginAutocomplete] = useState(null);

  const onOriginLoad = (autocomplete) => {
    setOriginAutocomplete(autocomplete);
  };

  const onOriginPlaceChanged = () => {
    if (!originAutocomplete) return;
    const place = originAutocomplete.getPlace();
    if (!place || !place.address_components) return;

    let city = "",
      state = "";
    for (const component of place.address_components) {
      if (component.types.includes("locality")) city = component.long_name;
      if (component.types.includes("administrative_area_level_1"))
        state = component.short_name;
    }

    if (city && state) {
      setPreferences((prev) => ({
        ...prev,
        origin_city: city,
        origin_state: state,
      }));
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser");
      return;
    }

    setError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`,
          );
          if (!response.ok) throw new Error("Failed to get location details");
          const data = await response.json();
          const address = data.address;

          let stateCode = address.state_code || "";
          if (
            !stateCode &&
            address.state &&
            STATE_NAME_TO_CODE[address.state]
          ) {
            stateCode = STATE_NAME_TO_CODE[address.state];
          }

          setPreferences((prev) => ({
            ...prev,
            origin_city: address.city || address.town || address.village || "",
            origin_state: stateCode,
          }));
        } catch (err) {
          setError("Error getting location details: " + err.message);
        }
      },
      (error) => {
        setError("Error getting location: " + error.message);
      },
    );
  };

  // Load existing preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const response = await axios.get(`${API_URL}/voice-preferences`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // Ensure we have the current user_id in the loaded preferences
        const userData = JSON.parse(localStorage.getItem("user"));
        setPreferences({
          ...response.data,
          user_id: userData?.email || "",
        });
      } catch (err) {
        console.error("Failed to load preferences:", err);
      }
    };
    loadPreferences();
  }, []);

  const validateForm = () => {
    // Only validate user-fillable required fields
    const requiredFields = [
      "phone_number",
      "truck_type",
      "origin_city",
      "origin_state",
    ];
    const emptyFields = requiredFields.filter((field) => !preferences[field]);

    // Check user_id separately since it's not user-fillable
    if (!preferences.user_id) {
      setError("User ID not found. Please try logging in again.");
      return false;
    }

    if (emptyFields.length > 0) {
      setError(
        `Please fill in all required fields: ${emptyFields.join(", ").replace(/_/g, " ")}`,
      );
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate form before submitting
    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem("access_token");

      await axios.post(`${API_URL}/voice-preferences`, preferences, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      // Handle validation errors
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          setError(
            err.response.data.detail.map((error) => error.msg).join(", "),
          );
        } else if (typeof err.response.data.detail === "object") {
          setError(Object.values(err.response.data.detail).join(", "));
        } else {
          setError(err.response.data.detail);
        }
      } else {
        setError("Failed to save preferences");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPreferences((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : type === "number"
            ? parseFloat(value) || 0
            : value,
    }));
    // Clear error when user starts typing
    if (error) setError(null);
  };

  return (
    <div className="ai-voice-container">
      <div className="hero-section">
        <h1>Cinesis AI Load Finder</h1>
        <p>Set up automated load notifications tailored to your preferences</p>
      </div>

      <div className="main-content">
        <div className="preferences-form-container">
          <div className="form-header">
            <h2>Load Search Preferences</h2>
            <p>
              We'll automatically search and notify you about matching loads
            </p>
          </div>

          <form onSubmit={handleSubmit} className="preferences-form">
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={preferences.is_active}
                  onChange={handleChange}
                />
                Enable Automated Load Notifications
              </label>
            </div>

            <div className="form-group">
              <label htmlFor="phone_number">Phone Number</label>
              <div className="input-with-icon">
                <i className="fas fa-phone"></i>
                <input
                  id="phone_number"
                  name="phone_number"
                  type="tel"
                  value={preferences.phone_number}
                  onChange={handleChange}
                  placeholder="(555) 555-5555"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="truck_type">Truck Type</label>
              <div className="input-with-icon">
                <i className="fas fa-truck"></i>
                <select
                  id="truck_type"
                  name="truck_type"
                  value={preferences.truck_type}
                  onChange={handleChange}
                >
                  {TRUCK_TYPES.map(({ code, label }) => (
                    <option key={code} value={code}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="origin_city">Origin City</label>
              <div className="input-with-icon">
                <i className="fas fa-city"></i>
                <div>
                  <Autocomplete
                    onLoad={onOriginLoad}
                    onPlaceChanged={onOriginPlaceChanged}
                    restrictions={{ country: "us" }}
                  >
                    <input
                      id="origin_city"
                      name="origin_city"
                      type="text"
                      value={preferences.origin_city}
                      onChange={handleChange}
                      placeholder="Enter city"
                      required
                      style={{ paddingRight: "40px", width: "100%" }}
                    />
                  </Autocomplete>
                  <div
                    onClick={getCurrentLocation}
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      color: "var(--primary)",
                      padding: "8px",
                      borderRadius: "50%",
                      transition: "background-color 0.2s",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    title="Use current location"
                  >
                    <i
                      className="fas fa-location-arrow"
                      style={{
                        color: "var(--primary) !important",
                        opacity: "1",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="origin_state">Origin State</label>
              <div className="input-with-icon">
                <i className="fas fa-map"></i>
                <input
                  id="origin_state"
                  name="origin_state"
                  type="text"
                  value={preferences.origin_state}
                  onChange={handleChange}
                  placeholder="Enter state (e.g., CA)"
                  required
                  maxLength="2"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="max_weight">Maximum Weight (lbs)</label>
              <div className="input-with-icon">
                <i className="fas fa-dumbbell"></i>
                <input
                  id="max_weight"
                  name="max_weight"
                  type="number"
                  value={preferences.max_weight}
                  onChange={handleChange}
                  placeholder="Enter maximum weight (lbs)"
                  min="0"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="min_rate_per_mile">
                Minimum Rate per Mile ($)
              </label>
              <div className="input-with-icon">
                <i className="fas fa-dollar-sign"></i>
                <input
                  id="min_rate_per_mile"
                  name="min_rate_per_mile"
                  type="number"
                  step="0.1"
                  value={preferences.min_rate_per_mile}
                  onChange={handleChange}
                  placeholder="Enter minimum rate"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="max_deadhead">Maximum Deadhead (miles)</label>
              <div className="input-with-icon">
                <i className="fas fa-road"></i>
                <input
                  id="max_deadhead"
                  name="max_deadhead"
                  type="number"
                  value={preferences.max_deadhead}
                  onChange={handleChange}
                  placeholder="Enter max deadhead"
                />
              </div>
            </div>

            <button type="submit" disabled={loading} className="submit-button">
              {loading ? "Saving..." : "Save Preferences"}
            </button>

            <button
              type="button"
              onClick={() => {
                const user = JSON.parse(localStorage.getItem("user"));
                fetch(
                  "https://pranav9.app.n8n.cloud/webhook/b93332d6-fc80-48db-b12a-b6bb4e3401db",
                  {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                      email: user.email,
                    }),
                  },
                )
                  .then((response) => {
                    if (!response.ok) throw new Error("Failed to trigger call");
                    alert("Call triggered successfully!");
                  })
                  .catch((err) => {
                    console.error("Error triggering call:", err);
                    alert("Failed to trigger call. Please try again.");
                  });
              }}
              className="submit-button"
              style={{ marginTop: "10px", backgroundColor: "#4CAF50" }}
            >
              Call Now
            </button>

            {error && (
              <div className="error-message">
                <i className="fas fa-exclamation-circle"></i>
                {error}
              </div>
            )}

            {success && (
              <div className="success-message">
                <i className="fas fa-check-circle"></i>
                Preferences saved successfully!
              </div>
            )}
          </form>
        </div>

        <div className="info-section">
          <h3>How It Works</h3>
          <div className="info-cards">
            <div className="info-card">
              <div className="info-icon">
                <i className="fas fa-robot"></i>
              </div>
              <h4>AI-Powered Search</h4>
              <p>
                Our AI continuously searches for loads matching your preferences
              </p>
            </div>
            <div className="info-card">
              <div className="info-icon">
                <i className="fas fa-phone-alt"></i>
              </div>
              <h4>Instant Notifications</h4>
              <p>
                Receive voice calls about matching loads as soon as they're
                available
              </p>
            </div>
            <div className="info-card">
              <div className="info-icon">
                <i className="fas fa-sliders-h"></i>
              </div>
              <h4>Customizable Settings</h4>
              <p>
                Fine-tune your preferences to get the most relevant load
                notifications
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIVoiceAgent;
